import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { showUpgradeExternally } from "./UpgradePrompt";
import { askForPremium } from "../lib/askPremium";
import { toast } from "sonner";

// Anything on the site can open the box with: openSongRequestBox()
export const OPEN_SONG_REQUEST = "dtm-open-song-request";
export const openSongRequestBox = () => window.dispatchEvent(new Event(OPEN_SONG_REQUEST));

// "Request a song". Premium members (and admins) get the form. Everyone else gets an invite:
// guests are asked to create a free login, free members are offered Premium (the pop-up goes on to Stripe).
// Mounted once, in components/Layout.js.
export default function SongRequestBox() {
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [style, setStyle] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const show = () => {
      const u = userRef.current;
      if (!u) {
        showUpgradeExternally("Request a song", {
          ctaLabel: "Create a free login",
          ctaPath: "/register",
          body: "Premium members can ask Dan to make songs they would love to hear. Create a free login first, then upgrade to Premium from your Account page.",
        });
        return;
      }
      if (!u.is_premium && u.role !== "admin") {
        askForPremium("Song requests are a Premium perk", {
          ctaLabel: "Upgrade to Premium",
          body: "Premium members can ask Dan for the songs they would love to hear. Upgrade to send your first request.",
        });
        return;
      }
      setMessage(""); setStyle(""); setSent(false); setBusy(false); setOpen(true);
    };
    window.addEventListener(OPEN_SONG_REQUEST, show);
    return () => window.removeEventListener(OPEN_SONG_REQUEST, show);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const send = async () => {
    if (message.trim().length < 3) { toast.error("Please tell Dan what song you would like."); return; }
    setBusy(true);
    try {
      await api.post("/song-requests", { message: message.trim(), style: style.trim(), page: window.location.pathname });
      setSent(true);
    } catch (e) {
      // A "premium_required" answer opens the Premium pop-up by itself (lib/api.js), so only other problems need a message.
      if (e.response?.data?.detail === "premium_required") setOpen(false);
      else toast.error(e.response?.data?.detail || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="song-request-box" role="dialog" aria-modal="true" aria-labelledby="song-request-title"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3" onClick={() => setOpen(false)}>
      <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-zinc-900 border border-amber-400/40 rounded-2xl p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div data-testid="song-request-thanks">
            <h2 id="song-request-title" className="font-display text-2xl font-bold">Thank you!</h2>
            <p className="text-sm text-zinc-300 mt-2">Dan got your request and reads every one.</p>
            <button type="button" onClick={() => setOpen(false)} data-testid="song-request-close" className="mt-4 px-5 py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300">Close</button>
          </div>
        ) : (
          <>
            <div className="font-mono-ui text-xs uppercase tracking-widest text-amber-400 mb-2">Premium perk</div>
            <h2 id="song-request-title" className="font-display text-2xl font-bold">Request a song</h2>
            <p className="text-sm text-zinc-400 mt-1 mb-3">Tell Dan about a song you would love to hear. A topic, a story, a feeling, or a kind of music.</p>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={4} autoFocus aria-label="The song you would like"
              placeholder="Example: a road trip song about driving through the Hill Country at sunset" data-testid="song-request-text"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />
            <input type="text" value={style} onChange={(e) => setStyle(e.target.value)} maxLength={200} aria-label="Style or mood (optional)"
              placeholder="Style or mood (optional), like country, upbeat, mellow" data-testid="song-request-style"
              className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />
            <p className="text-xs text-zinc-500 mt-3">Requests are ideas only. Dan writes and chooses the lyrics himself, so every song stays original. Please don't send lyrics or ask for copies of other artists' songs. He can't promise every request becomes a song, but he reads them all. Never sold or shared for ads. <Link to="/privacy" className="text-cyan-400 hover:underline" onClick={() => setOpen(false)}>Privacy</Link></p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={busy} onClick={send} data-testid="song-request-send" className="px-5 py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-60">{busy ? "Sending..." : "Send request"}</button>
              <button type="button" disabled={busy} onClick={() => setOpen(false)} data-testid="song-request-cancel" className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/5">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
